import classNames from 'classnames'
import { isDate } from 'lodash-es'

import { Avatar } from '@/Atomic/Molecules/Avatar'
import { DataBinary } from '@/utils/dataBinaryToUrl'
import { formatTimeAgo } from '@/utils/formatTimeAgo'

import './TimelineItemHeader.scss'

interface TimelineItemHeaderProps {
  /**
   * What happened, for example "Demo created by Camille Moreau" or
   * "Email opened". Localized by the caller and rendered emphasized. Wrap an
   * actor name inside the sentence in `TimelineItemHeaderActor`, so the name
   * keeps its own typography.
   */
  action: React.ReactNode
  /** Moment the entry last changed, rendered as relative time ("2 hours ago"). */
  changedAt?: string | Date
  /** Avatar of the actor. Falls back to the initials, then to a generic icon. */
  actorAvatar?: string | DataBinary
  actorInitials?: string
  /**
   * Stable id of the actor. Keeps the initials background color stable and marks
   * the entry as caused by a person, which is what shows the avatar for an actor
   * without an image or initials.
   */
  actorId?: string
  /** Trailing content, for example a details toggle of a grouped entry. */
  trailing?: React.ReactNode
  className?: string
}

/**
 * Single line above a timeline entry telling what changed and when.
 *
 * Renders `[avatar] [action] [relative time] [trailing]`. Everything except the
 * action is optional, so the same header serves activity entries (whose actor is
 * named inside the action) and system entries such as email or automation events
 * (action and time only). The leading type icon is not part of this component,
 * it belongs to the surrounding timeline.
 */
export const TimelineItemHeader: React.FC<TimelineItemHeaderProps> = ({
  action,
  changedAt,
  actorAvatar,
  actorInitials,
  actorId,
  trailing,
  className,
}) => {
  const timeAgo = formatTimeAgo(changedAt)
  const hasActor = !!(actorAvatar || actorInitials || actorId)

  return (
    <div className={classNames('m-timeline-item-header', className)}>
      {hasActor && (
        <Avatar
          avatar={actorAvatar}
          initials={actorInitials}
          initialsColorSeed={actorId}
          icon="user"
          size="sm"
          className="m-timeline-item-header__avatar"
        />
      )}
      <div className="m-timeline-item-header__text">
        <span className="m-timeline-item-header__action">{action}</span>
        {timeAgo && (
          <time
            className="m-timeline-item-header__time"
            dateTime={toIsoString(changedAt)}
          >
            {timeAgo}
          </time>
        )}
      </div>
      {trailing && (
        <div className="m-timeline-item-header__trailing">{trailing}</div>
      )}
    </div>
  )
}

/**
 * Actor name inside an `action` sentence, for example the person behind "Demo
 * created by Camille Moreau". Keeps the name visually secondary to the described
 * change, and lets the caller localize the whole sentence in one string.
 */
export const TimelineItemHeaderActor: React.FC<{
  children?: React.ReactNode
}> = ({ children }) => (
  <span className="m-timeline-item-header__actor">{children}</span>
)

const toIsoString = (changedAt?: string | Date) => {
  return isDate(changedAt) ? changedAt.toISOString() : changedAt
}
